import React, { Fragment, lazy, Suspense, useEffect, useState } from 'react';
import i18next from 'i18next';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import SMResult from 'components/SMResult/SMResult';
import AuthLayout from './Auth';
import queryString from 'query-string';
import { isEmpty as _isEmpty, delay as _delay } from 'lodash-es';
import OAuth from 'pages/OAuth';
import AuthWrapper from './AuthWrapper';
import { routesMapping } from 'utilities/utilities_exports';
import TagManager from 'react-gtm-module';
import { useLocation } from 'react-router-dom';
import PageSkeleton from './PageSkeleton/PageSkeleton';

const ForgotPasswordContainer = lazy(() =>
  import(/* webpackChunkName: 'ForgotPasswordContainer' */ 'pages/ForgotPasswordContainer')
);

const SignUpContainer = lazy(() => import(/* webpackChunkName: 'SignUpContainer' */ 'pages/SignUpContainer'));

const CookiesPolicy = lazy(() => import(/* webpackChunkName: 'CookiesPolicy' */ 'ExternalPages/CookiesPolicy'));
const PrivacyPolicy = lazy(() => import(/* webpackChunkName: 'PrivacyPolicy' */ 'ExternalPages/PrivacyPolicy'));
const TermConditions = lazy(() => import(/* webpackChunkName: 'TermConditions' */ 'ExternalPages/TermConditions'));

const DemoLogin = lazy(() => import(/* webpackChunkName : 'Demo-Loader' */ 'Modules/Demo/DemoLoader'));

const Layouts = {
  PageSkeleton: PageSkeleton,
  auth: AuthLayout
};

const Layout = props => {
  const { loading } = props;
  const location = useLocation();

  const [initialSlideNumber, setInitialSlideNumber] = useState(0);
  const user = useSelector(state => state?.User);
  // const pathname = useSelector(state => state?.router?.location?.pathname);
  const pathname = location.pathname

  // Layout Rendering
  const getLayout = () => {
    if (/^\/login(?=\/|$)/i.test(pathname)) {
      return 'auth';
    }
    if(/^\/demo-login(?=\/|$)/i.test(pathname))
    {
      return 'demo-login';
    }
    if (/^\/forgotPassword(?=\/|$)/i.test(pathname)) {
      return 'forgotPassword';
    }
    if (/^\/signup(?=\/|$)/i.test(pathname)) {
      return 'signup';
    }
    if (/^\/privacyPolicy(?=\/|$)/i.test(pathname)) {
      return 'privacyPolicy';
    }
    if (/^\/cookiePolicy(?=\/|$)/i.test(pathname)) {
      return 'cookiePolicy';
    }
    if (/^\/termConditions(?=\/|$)/i.test(pathname)) {
      return 'termConditions';
    }
    return 'PageSkeleton';
  };

  const Container = Layouts[getLayout()];
  const isUserAuthorized = user.authorized;
  const isUserLoading = user.loading;
  const isAuthLayout = getLayout() === 'auth';
  const isForgotPassword = getLayout() === 'forgotPassword';
  const isSignUp = getLayout() === 'signup';
  const isDemo = getLayout() === 'demo-login';

  const isPrivacyPolicy = getLayout() === 'privacyPolicy';
  const isCookiePolicy = getLayout() === 'cookiePolicy';
  const isTermCondition = getLayout() === 'termConditions';

  // *--- Shopify OAuth
  const getOAuthQueryParameters = () => {
    const parsedQueryString = queryString.parse(window.location.search);
    if (
      !_isEmpty(parsedQueryString?.shop) &&
      !_isEmpty(parsedQueryString?.hmac) &&
      !_isEmpty(parsedQueryString?.timestamp) &&
      !_isEmpty(parsedQueryString?.host) &&
      _isEmpty(parsedQueryString?.session)
    ) {
      return window.location.search;
    } else if (
      !_isEmpty(parsedQueryString?.code) &&
      !_isEmpty(parsedQueryString?.state) &&
      !_isEmpty(parsedQueryString?.action)
    ) {
      // for lazada connect
      return window.location.search;
    } else {
      return '';
    }
  };

  const getLocalizationFilePath = () => {
    return `${process.env.REACT_APP_LOCALIZATION_SERVER_URL}/locales/resources/default/Turbocharger_{{lng}}.json?`;
  };

  const BootstrappedLayout = () => {
    // show loader when user in check authorization process, not authorized yet and not on login pages
    if (
      isUserLoading &&
      !isUserAuthorized &&
      !isAuthLayout &&
      !isForgotPassword &&
      !isCookiePolicy &&
      !isPrivacyPolicy &&
      !isTermCondition
    ) {
      return <SMResult type='loader' message='Loading experience' forChart />;
    }
    const parsedQueryString = queryString.parse(window.location.search);
    // const backendInstance = i18next.services.backendConnector.backend;
    // backendInstance.options.loadPath = getLocalizationFilePath();
    // i18next.init(i18next.options);
    if (parsedQueryString?.code) {
      if (isAuthLayout) {
        return (
          <Suspense fallback={<></>}> 
            <AuthLayout>{props.children}</AuthLayout>
          </Suspense>
        );
      }
      if (isSignUp) {
        return (
          <AuthWrapper initialSlideNumber={initialSlideNumber}>
            <Suspense fallback={<></>}>
              <SignUpContainer setInitialSlideNumber={setInitialSlideNumber} />
            </Suspense>
          </AuthWrapper>
        );
      }
    }

    // *This step is to clear cookies everytime when the user opens the demo-login page.
    if(isDemo)
    {
      return(
      <AuthWrapper>
        <Suspense fallback={<div>Loading demo login...</div>}>
          <DemoLogin />
        </Suspense>
      </AuthWrapper>
      );
    }
    // * if user is logged out then only he can access login, signup, password and demo-login pages.
    if (!isAuthLayout && !isUserAuthorized) {
      if (!isForgotPassword && !isSignUp && !isPrivacyPolicy && !isCookiePolicy && !isTermCondition && !isDemo) {
        const oAuthParams = getOAuthQueryParameters();
        let newUrl = `/login${oAuthParams ? oAuthParams : ''}`;
        let isManualLogout = sessionStorage.getItem('isManualLogout') || false;
        if (!oAuthParams && !isManualLogout) {
          newUrl = `/login?redirectFrom=${location.pathname}${location.search ? location.search : ''}`;
        }
        sessionStorage.clear();
        window.history.pushState(null, '', newUrl);
        window.location.reload();
      }
      if(isDemo)
      {
        return(
        <AuthWrapper>
          <Suspense fallback={<div>Loading demo login...</div>}>
            <DemoLogin />
          </Suspense>
        </AuthWrapper>
        );
      }
      if (isForgotPassword) {
        return (
          <AuthWrapper>
            <Suspense fallback={<></>}>
              <ForgotPasswordContainer />
            </Suspense>
          </AuthWrapper>
        );
      }
      if (isSignUp) {
        return (
          <AuthWrapper initialSlideNumber={initialSlideNumber}>
            <Suspense fallback={<></>}>
              <SignUpContainer setInitialSlideNumber={setInitialSlideNumber} />
            </Suspense>
          </AuthWrapper>
        );
      }
    }
    if (isCookiePolicy) {
      return (
        <Suspense fallback={<></>}>
          <CookiesPolicy />
        </Suspense>
      );
    }
    if (isPrivacyPolicy) {
      return (
        <Suspense fallback={<></>}>
          <PrivacyPolicy />
        </Suspense>
      );
    }
    if (isTermCondition) {
      return (
        <Suspense fallback={<></>}>
          <TermConditions />
        </Suspense>
      );
    }
    // * if user is logged in then he should not access login, signup, demo-login and password pages.
    if (isUserAuthorized && (isAuthLayout || isSignUp || isForgotPassword || isDemo)) {
      const oAuthParams = getOAuthQueryParameters();
      if (!_isEmpty(oAuthParams)) {
        return <OAuth></OAuth>;
      }
      window.history.pushState(null, '', routesMapping['connect']);
      window.location.reload();
    }

    // in other case render previously set layout
    return (
      <Container loading={loading} pathName={pathname}>
        {props.children}
      </Container>
    );
  };

  // * Handled chunk failed error. just refresh one time again
  const handleReloadOnError = () => {
    const hasRefreshed = sessionStorage.getItem('retry-lazy-refreshed') || 'false';
    try {
      if (hasRefreshed && hasRefreshed === 'true') {
        sessionStorage.setItem('retry-lazy-refreshed', 'false');
      } else {
        throw new Error();
      }
    } catch (error) {
      sessionStorage.setItem('retry-lazy-refreshed', 'true'); // we are now going to refresh

      _delay(() => {
        return window.location.reload(); // refresh the page
      }, 400);
    }
  };

  useEffect(() => {
    if (process.env.REACT_APP_GTM_ID && process.env.NODE_ENV === 'production') {
      TagManager.initialize({
        gtmId: process.env.REACT_APP_GTM_ID
      });
    }
  }, []);

  return (
    <Fragment>
      <Helmet titleTemplate='%s | Graas ' title='Dashboard'>
        <script type='text/javascript' defer>
          {`
            window._mfq = window._mfq || [];
            (function(i) {
              var mf = document.createElement("script");
              mf.type = "text/javascript"; mf.defer = true;
              mf.src = "//cdn.mouseflow.com/projects/"+i+".js";
              document.getElementsByTagName("head")[0].appendChild(mf);
              console.log('Mouseflow Pixel fired with Graas dashboard 🔥');
            })("${process.env.REACT_APP_MOUSEFLOW_KEY}");
          `}
        </script>
        <script type='text/javascript'>
          {window.addEventListener('error', e => {
            if (/Loading chunk [\d]+ failed/.test(e.message)) {
              handleReloadOnError();
            }
          })}
        </script>
      </Helmet>

      {BootstrappedLayout()}
    </Fragment>
  );
};

export default Layout;
